import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  Tag,
  Modal,
  Typography,
  Popconfirm,
  Select,
  Space,
} from "antd";
import { callApi } from "../../api/apiCaller";
import routes from "../../api/routes";
import Loader from "../../components/loader/loader";
import { GreenNotify, RedNotify } from "../../helper/helper";

const { Paragraph } = Typography;
const { Option } = Select;

const ReportedPostsList = () => {
  const [reports, setReports] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [isResolveModalVisible, setIsResolveModalVisible] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");

  const fetchReports = async () => {
    await callApi(
      "GET",
      routes.getAllReports,
      null,
      setIsLoading,
      (res) => {
        setReports(res.reports || []);
      },
      (err) => RedNotify(err?.message || "Failed to fetch reports.")
    );
  };

  useEffect(() => {
    fetchReports();
  }, []);

  const handleResolveClick = (report) => {
    setSelectedReport(report);
    setIsResolveModalVisible(true);
  };

  const handleConfirmResolve = async () => {
    if (!selectedReport) return;

    await callApi(
      "PUT",
      `${routes.resolveReport}/${selectedReport._id}`,
      { isResolved: true },
      () => {},
      () => {
        GreenNotify("Report marked as resolved.");
        setIsResolveModalVisible(false);
        fetchReports();
      },
      (err) => RedNotify(err?.message || "Failed to resolve report.")
    );
  };

  const handleDeletePost = async (report) => {
    const postId = report.post?._id;
    if (!postId) {
      return RedNotify("Post or user ID not found.");
    }

    try {
      await callApi(
        "DELETE",
        `${routes.deletePost}/${postId}`,
        null,
        () => {},
        async () => {
          GreenNotify("Post deleted successfully.");
          fetchReports();
        },
        (err) => RedNotify(err?.message || "Failed to delete post.")
      );
    } catch (error) {
      RedNotify("Something went wrong.");
    }
  };

  const getFilteredReports = () => {
    if (statusFilter === "resolved") {
      return reports.filter((report) => report.isResolved);
    }
    if (statusFilter === "pending") {
      return reports.filter((report) => !report.isResolved);
    }
    return reports;
  };

  const columns = [
    {
      title: "Post",
      dataIndex: "post",
      render: (post) =>
        post && post._id ? (
          <p>
            {post.content
              ? post.content.length > 50
                ? post.content.slice(0, 50) + "..."
                : post.content
              : post._id}
          </p>
        ) : (
          "N/A"
        ),
    },
    {
      title: "Reporter",
      dataIndex: ["reporter", "email"],
      render: (email) => email || "N/A",
    },
    {
      title: "Reason",
      dataIndex: "reason",
    },
    {
      title: "Status",
      dataIndex: "isResolved",
      render: (resolved) =>
        resolved ? (
          <Tag color="green">Resolved</Tag>
        ) : (
          <Tag color="red">Pending</Tag>
        ),
    },
    {
      title: "Action",
      render: (_, report) => {
        if (!report.post) {
          return <Tag color="gray">Post Deleted</Tag>;
        }

        return (
          <div style={{ display: "flex", gap: "8px" }}>
            {!report.isResolved && (
              <Button
                type="primary"
                style={{ marginRight: 8 }}
                onClick={() => handleResolveClick(report)}
              >
                Mark as Resolved
              </Button>
            )}

            <Popconfirm
              title="Are you sure you want to delete this post?"
              onConfirm={() => handleDeletePost(report)}
              okText="Yes, Delete"
              cancelText="Cancel"
            >
              <Button danger>Delete Post</Button>
            </Popconfirm>
          </div>
        );
      },
    },
  ];

  return (
    <div className="admin-reports-container">
      <div>
        <div
          style={{
            padding: "16px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "16px",
          }}
        >
          <h1 style={{ margin: "0" }}>Reported Posts</h1>
          <Select
            defaultValue="all"
            style={{ width: 200 }}
            onChange={setStatusFilter}
          >
            <Option value="all">All Reports</Option>
            <Option value="resolved">Resolved</Option>
            <Option value="pending">Pending</Option>
          </Select>
        </div>
        {/* The rest of your component's JSX */}
      </div>

      <Table
        dataSource={getFilteredReports().map((r) => ({ ...r, key: r._id }))}
        columns={columns}
        loading={isLoading}
        pagination={{ pageSize: 10 }}
      />

      <Modal
        visible={isResolveModalVisible}
        title="Resolve Report"
        onCancel={() => setIsResolveModalVisible(false)}
        onOk={handleConfirmResolve}
        okText="Mark as Resolved"
      >
        {selectedReport && (
          <>
            <Paragraph>
              <strong>Post ID:</strong> {selectedReport.post?._id}
            </Paragraph>
            <Paragraph>
              <strong>Post Content:</strong>{" "}
              {selectedReport.post?.content || "N/A"}
            </Paragraph>
            <Paragraph>
              <strong>Reporter:</strong>{" "}
              {selectedReport.reporter?.email || "N/A"}
            </Paragraph>
            <Paragraph>
              <strong>Reason:</strong> {selectedReport.reason || "N/A"}
            </Paragraph>
          </>
        )}
      </Modal>

      <Loader loading={isLoading} />
    </div>
  );
};

export default ReportedPostsList;
